import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Settings, LogOut, ChevronRight, ChevronDown, ArrowLeft,
  Users, Calendar, Gift, MessageCircle, DollarSign, UsersRound, Menu, Pill,
  Wrench, Boxes,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useApplyTenantBranding, useTerm } from "@/contexts/branding";
import { TID } from "@/lib/testIds";
import ThemeToggle from "@/components/ThemeToggle";
import StatusReminderBell from "@/components/StatusReminderBell";
import NotificationBell from "@/components/NotificationBell";
import DailyBriefingModal from "@/components/DailyBriefingModal";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import CopilotDrawer from "@/components/CopilotDrawer";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const ROLE_LABELS = {
  master_admin: "Super Administrador",
  tenant_admin: "Administrador",
  manager: "Gerente",
  staff: "Funcionário",
  portal_client: "Cliente",
};

export default function TenantLayout() {
  const auth = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useApplyTenantBranding(auth?.tenant?.branding);

  useEffect(() => {
    if (auth?.tenant?.slug && auth.tenant.slug !== slug) {
      nav(`/t/${auth.tenant.slug}/dashboard`, { replace: true });
    }
  }, [auth?.tenant?.slug, slug, nav]);

  // Fecha o drawer sempre que a rota mudar (evita ficar aberto após clicar num link)
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const logout = async () => {
    // SECURITY: Only honour impersonation-return when the JWT actually carries
    // impersonated_by (server truth). Plain funcionários never get this claim,
    // so they always do a normal logout — even if stale localStorage exists.
    const wasImpersonating = !!auth?.impersonatedBy && !!localStorage.getItem("gc_master_token_backup");
    if (wasImpersonating) {
      const back = localStorage.getItem("gc_master_token_backup");
      localStorage.setItem("gc_token", back);
      localStorage.removeItem("gc_master_token_backup");
      window.location.assign("/master/tenants");
      return;
    }
    localStorage.removeItem("gc_master_token_backup");
    await auth.logout();
    nav(`/t/${slug}/login`, { replace: true });
  };

  const flags = auth?.tenant?.feature_flags || {};
  const role = auth?.user?.role;
  const isAdmin = role === "tenant_admin" || role === "master_admin";
  const canManageSettings = isAdmin || role === "manager";
  const term = useTerm(auth?.tenant);

  const rawNav = [
    // Item simples (link direto)
    { kind: "link", to: `/t/${slug}/dashboard`, label: "Visão Geral", icon: LayoutDashboard, tid: TID.tenantNavDashboard, flag: "dashboard" },

    // Menu Clientes
    { kind: "group", label: term("client_plural"), icon: Users, tid: "tenant-nav-clientes-group",
      match: (path) => path.includes("/clientes") || path.includes("/recorrencia") || path.includes("/gestor-de-risco") || path.includes("/nps"),
      children: [
        { to: `/t/${slug}/clientes`, label: `Lista de ${term("client_plural")}`, tid: "tenant-nav-clientes", flag: "clientes" },
        { to: `/t/${slug}/recorrencia`, label: "Recorrência", tid: "tenant-nav-recorrencia", flag: "clientes", verticalExcept: ["estudio"] },
        { to: `/t/${slug}/gestor-de-risco`, label: "Gestor de Risco", tid: "tenant-nav-risk", flag: "clientes", verticalExcept: ["estudio"] },
        { to: `/t/${slug}/nps`, label: "Pesquisa de Satisfação", tid: "tenant-nav-nps", flag: "pesquisa_satisfacao" },
      ],
    },

    // Equipe (direto)
    { kind: "link", to: `/t/${slug}/equipe`, label: "Equipe", icon: UsersRound, tid: "tenant-nav-equipe", flag: null, adminOnly: true },

    // Menu Agenda
    { kind: "group", label: "Agenda", icon: Calendar, tid: "tenant-nav-agenda-group",
      match: (path) => path.includes("/agenda"),
      children: [
        { to: `/t/${slug}/agenda`, label: "Calendário", tid: "tenant-nav-agenda", flag: "agenda", end: true },
        { to: `/t/${slug}/agenda?view=list`, label: "Lista de Atendimentos", tid: "tenant-nav-agenda-list", flag: "agenda",
          matchQuery: (search) => new URLSearchParams(search).get("view") === "list" },
      ],
    },

    // Medicações (só pet)
    { kind: "link", to: `/t/${slug}/medicacoes`, label: "Medicações", icon: Pill, tid: "tenant-nav-medicacoes", flag: null, verticalOnly: "pet" },

    // Menu Serviços
    { kind: "group", label: "Serviços", icon: Wrench, tid: "tenant-nav-services-group",
      match: (path, search) => path.endsWith("/financeiro") && ["services","packages","plans"].includes(new URLSearchParams(search).get("tab") || "services"),
      children: [
        { to: `/t/${slug}/financeiro?tab=services`, label: "Lista de Serviços", tid: "tenant-nav-services", flag: "financeiro",
          matchQuery: (s) => (new URLSearchParams(s).get("tab") || "services") === "services" },
        { to: `/t/${slug}/financeiro?tab=packages`, label: "Pacotes", tid: "tenant-nav-packages", flag: "financeiro",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "packages" },
        { to: `/t/${slug}/financeiro?tab=plans`, label: "Planos", tid: "tenant-nav-plans", flag: "financeiro",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "plans" },
      ],
    },

    // Menu Estoque
    { kind: "group", label: "Estoque", icon: Boxes, tid: "tenant-nav-inventory-group",
      match: (path, search) => path.endsWith("/financeiro") && new URLSearchParams(search).get("tab") === "inventory",
      children: [
        { to: `/t/${slug}/financeiro?tab=inventory`, label: "Painel de Estoque", tid: "tenant-nav-inv-dashboard", flag: "financeiro",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "inventory" && !new URLSearchParams(s).get("sub") },
        { to: `/t/${slug}/financeiro?tab=inventory&sub=products`, label: "Produtos", tid: "tenant-nav-inv-products", flag: "financeiro",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "inventory" && new URLSearchParams(s).get("sub") === "products" },
        { to: `/t/${slug}/financeiro?tab=inventory&sub=suppliers`, label: "Fornecedores", tid: "tenant-nav-inv-suppliers", flag: "financeiro",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "inventory" && new URLSearchParams(s).get("sub") === "suppliers" },
      ],
    },

    // Menu Fidelidade — Regras/Rewards/Tx/Redeem em /fidelidade, Clube em /clube, Indicações em /indicacao
    { kind: "group", label: "Fidelidade", icon: Gift, tid: "tenant-nav-loyalty-group",
      match: (path) => path.includes("/fidelidade") || path.includes("/clube") || path.includes("/indicacao"),
      children: [
        { to: `/t/${slug}/fidelidade?tab=rules`, label: "Regras", tid: "tenant-nav-loy-rules", flag: "fidelidade",
          matchQuery: (s) => (new URLSearchParams(s).get("tab") || "rules") === "rules" },
        { to: `/t/${slug}/fidelidade?tab=rewards`, label: "Recompensas", tid: "tenant-nav-loy-rewards", flag: "fidelidade",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "rewards" },
        { to: `/t/${slug}/fidelidade?tab=transactions`, label: "Transações de Pontos", tid: "tenant-nav-loy-tx", flag: "fidelidade",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "transactions" },
        { to: `/t/${slug}/fidelidade?tab=redeem`, label: "Resgate", tid: "tenant-nav-loy-redeem", flag: "fidelidade",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "redeem" },
        { to: `/t/${slug}/clube`, label: "Clube de Benefícios", tid: "tenant-nav-loy-club", flag: "fidelidade" },
        { to: `/t/${slug}/indicacao`, label: "Indicações", tid: "tenant-nav-loy-ref", flag: "fidelidade" },
      ],
    },

    // Menu Mensagens
    { kind: "group", label: "Mensagens", icon: MessageCircle, tid: "tenant-nav-mensagens-group",
      match: (path) => path.includes("/mensagens") || path.includes("/campanhas-prontas"),
      children: [
        { to: `/t/${slug}/mensagens`, label: "Chat de Mensagens", tid: "tenant-nav-mensagens", flag: "campanhas" },
        { to: `/t/${slug}/campanhas-prontas`, label: "Campanhas Prontas", tid: "tenant-nav-campaigns", flag: "campanhas" },
      ],
    },

    // Menu Financeiro (histórico/assinaturas)
    { kind: "group", label: "Financeiro", icon: DollarSign, tid: "tenant-nav-financeiro-group",
      match: (path, search) => path.endsWith("/financeiro") && ["payments","subscriptions"].includes(new URLSearchParams(search).get("tab")),
      children: [
        { to: `/t/${slug}/financeiro?tab=payments`, label: "Histórico de Pagamentos", tid: "tenant-nav-fin-payments", flag: "financeiro",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "payments" },
        { to: `/t/${slug}/financeiro?tab=subscriptions`, label: "Assinaturas / Mensalidades", tid: "tenant-nav-fin-subs", flag: "financeiro",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "subscriptions" },
      ],
    },

    // Menu Configurações
    { kind: "group", label: "Configurações", icon: Settings, tid: "tenant-nav-settings-group",
      adminOnly: !canManageSettings,
      match: (path) => path.includes("/settings"),
      children: [
        { to: `/t/${slug}/settings?tab=branding`, label: "Identidade Visual", tid: "tenant-nav-cfg-branding", flag: "configuracoes",
          matchQuery: (s) => (new URLSearchParams(s).get("tab") || "branding") === "branding" },
        { to: `/t/${slug}/settings?tab=hours`, label: "Funcionamento", tid: "tenant-nav-cfg-hours", flag: "configuracoes",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "hours" },
        { to: `/t/${slug}/settings?tab=access`, label: "Acessos", tid: "tenant-nav-cfg-access", flag: "configuracoes",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "access" },
        { to: `/t/${slug}/settings?tab=modules`, label: "Recursos", tid: "tenant-nav-cfg-modules", flag: "configuracoes",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "modules" },
        { to: `/t/${slug}/settings?tab=integrations`, label: "Integrações", tid: "tenant-nav-cfg-integr", flag: "configuracoes",
          matchQuery: (s) => ["integrations", "whatsapp"].includes(new URLSearchParams(s).get("tab")) },
        { to: `/t/${slug}/settings?tab=landing`, label: "Landing Pública", tid: "tenant-nav-cfg-landing", flag: "configuracoes",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "landing" },
        { to: `/t/${slug}/settings?tab=history`, label: "Histórico de Alterações", tid: "tenant-nav-cfg-history", flag: "configuracoes",
          matchQuery: (s) => new URLSearchParams(s).get("tab") === "history" },
      ],
    },
  ];

  const b = auth?.tenant?.branding || {};
  const isImpersonating = !!auth?.impersonatedBy && !!localStorage.getItem("gc_master_token_backup");

  const brandBlock = (
    <div className="px-5 py-5 border-b border-border flex items-center gap-2.5">
      {b.logo_url ? (
        <img src={b.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover" />
      ) : (
        <div className="w-9 h-9 rounded-lg grid place-items-center font-display font-bold text-tenant-foreground"
          style={{ background: `hsl(var(--tenant-primary))` }}>
          {(b.display_name || auth?.tenant?.legal_name || "·").charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <div className="font-display font-bold leading-none truncate">{b.display_name || auth?.tenant?.legal_name}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1 truncate">{auth?.tenant?.vertical}</div>
      </div>
    </div>
  );

  const currentPath = location.pathname;
  const currentSearch = location.search;

  const passesGuards = (n) => {
    const vertical = (auth?.tenant?.vertical || "").toLowerCase();
    return (
      (n.flag === null || n.flag === undefined || flags[n.flag] === true) &&
      (!n.adminOnly || isAdmin) &&
      (!n.verticalOnly || vertical === n.verticalOnly) &&
      (!n.verticalExcept || !n.verticalExcept.includes(vertical))
    );
  };

  const navItems = rawNav
    .map((n) => {
      if (n.kind === "group") {
        if (!passesGuards(n)) return null;
        const kids = (n.children || []).filter(passesGuards);
        return kids.length > 0 ? { ...n, children: kids } : null;
      }
      return passesGuards(n) ? n : null;
    })
    .filter(Boolean);


  const isGroupActive = (g) => {
    try { return typeof g.match === "function" && g.match(currentPath, currentSearch); }
    catch { return false; }
  };

  const isChildActive = (child) => {
    // Compara path SEM query, e depois valida query se matchQuery estiver setado
    const [childPath] = child.to.split("?");
    if (childPath !== currentPath) return false;
    if (typeof child.matchQuery === "function") return child.matchQuery(currentSearch);
    // Sem matchQuery: só path
    return child.end ? !currentSearch : true;
  };

  // controla accordion aberto: abre automaticamente o grupo ativo
  const [openGroup, setOpenGroup] = useState(() => {
    const active = navItems.find((n) => n.kind === "group" && isGroupActive(n));
    return active ? active.label : null;
  });
  // Se navegação mudou e nenhum grupo está aberto mas há um ativo, abrir
  useEffect(() => {
    const active = navItems.find((n) => n.kind === "group" && isGroupActive(n));
    if (active && openGroup !== active.label) setOpenGroup(active.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, currentSearch]);

  const navList = (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="tenant-nav-list">
      {navItems.map((n) => {
        if (n.kind === "link") {
          return (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "text-tenant-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
              style={({ isActive }) => isActive ? { background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` } : {}}
              onClick={() => { setMobileNavOpen(false); }}
            >
              <n.icon className="w-4 h-4" strokeWidth={1.6} />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          );
        }
        // group
        const groupActive = isGroupActive(n);
        const isOpen = openGroup === n.label;
        return (
          <div key={n.label} data-testid={n.tid}>
            <button
              type="button"
              onClick={() => setOpenGroup(isOpen ? null : n.label)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                groupActive ? "text-foreground bg-muted" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              data-testid={`${n.tid}-toggle`}
              aria-expanded={isOpen}
            >
              <n.icon className="w-4 h-4" strokeWidth={1.6} />
              <span className="font-medium flex-1 text-left">{n.label}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.6} />
            </button>
            {isOpen && (
              <div className="ml-4 pl-3 border-l border-border mt-1 mb-2 space-y-0.5" data-testid={`${n.tid}-children`}>
                {n.children.map((c) => {
                  const active = isChildActive(c);
                  return (
                    <NavLink
                      key={c.tid}
                      to={c.to}
                      data-testid={c.tid}
                      className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                        active ? "text-tenant-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                      style={active ? { background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` } : {}}
                      onClick={() => { setMobileNavOpen(false); }}
                    >
                      {c.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  const userFooter = (
    <div className="px-3 py-4 border-t border-border space-y-2">
      <div className="px-2 pb-1 space-y-0.5" data-testid="tenant-user-info">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold truncate" data-testid="tenant-user-name">
            {auth?.user?.name || "Usuário"}
          </div>
          {/* Toggle de tema fica aqui no rodapé; os sinos ficam no header. */}
          <div className="flex items-center gap-1">
            <ThemeToggle testId="tenant-theme-toggle" />
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground truncate" data-testid="tenant-user-email">
          {auth?.user?.email}
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/80" data-testid="tenant-user-role">
          {ROLE_LABELS[role] || role}
        </div>
      </div>
      <button onClick={logout} data-testid={TID.tenantLogout}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          {isImpersonating ? <ArrowLeft className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
          {isImpersonating ? "Voltar para a Matriz" : "Sair"}
        </span>
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  return (
    <NotificationsProvider>
    <div className="min-h-screen bg-background lg:flex">
      {/* Sidebar desktop (≥ lg) */}
      <aside
        data-testid={TID.tenantSidebar}
        className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card flex-col"
      >
        {brandBlock}
        {navList}
        {userFooter}
      </aside>

      {/* Drawer mobile (< lg) */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="p-0 w-72 max-w-[85vw] flex flex-col bg-card"
          data-testid="tenant-mobile-nav"
        >
          <VisuallyHidden>
            <SheetHeader>
              <SheetTitle>Menu de navegação</SheetTitle>
              <SheetDescription>Acesse as áreas do painel</SheetDescription>
            </SheetHeader>
          </VisuallyHidden>
          {brandBlock}
          {navList}
          {userFooter}
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top bar mobile (< lg) — hamburger + branding compacto + sinos */}
        <header className="lg:hidden sticky top-0 z-40 flex items-center gap-2 px-3 py-2 border-b border-border bg-card/95 backdrop-blur">
          <button
            type="button"
            data-testid="tenant-mobile-menu-btn"
            aria-label="Abrir menu"
            className="w-9 h-9 rounded-md grid place-items-center hover:bg-muted transition-colors"
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu className="w-5 h-5" strokeWidth={1.8} />
          </button>

          <div className="flex-1 min-w-0 flex items-center gap-2">
            {b.logo_url ? (
              <img src={b.logo_url} alt="" className="w-7 h-7 rounded-md object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-md grid place-items-center font-display font-bold text-tenant-foreground text-sm"
                style={{ background: `hsl(var(--tenant-primary))` }}>
                {(b.display_name || auth?.tenant?.legal_name || "·").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="font-display font-semibold text-sm truncate">
              {b.display_name || auth?.tenant?.legal_name}
            </div>
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            <NotificationBell />
            <StatusReminderBell />
          </div>
        </header>

        {/* Sinos ficam no rodapé do sidebar desktop também, pra continuidade */}
        <div className="hidden lg:flex items-center justify-end gap-1 px-4 py-2 border-b border-border bg-card/60">
          <NotificationBell />
          <StatusReminderBell />
        </div>

        {isImpersonating && (
          <div className="px-4 py-2 text-xs text-amber-900 bg-amber-100 border-b border-amber-200 flex items-center justify-between gap-2">
            <span className="line-clamp-2">Você está entrando como administrador desta empresa (modo visualização da matriz).</span>
            <button onClick={logout} className="underline shrink-0">Voltar para a Matriz</button>
          </div>
        )}
        <Outlet />
      </main>

      {/* Copiloto IA — flutuante */}
      <CopilotDrawer />
      {/* Modal proativo diário */}
      <DailyBriefingModal />
    </div>
    </NotificationsProvider>
  );
}
